import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../auth';
import { CheckCircle, Copy, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) => `₩${Number(v || 0).toLocaleString('ko-KR')}`;

export default function OrderSuccessView({ success, t, navigate, setSuccess }) {
  const [bank, setBank] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => setBank(r.data)).catch(() => {});
  }, []);

  const copy = (text, label = 'Скопировано') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="order-success">
      <Card className="p-8">
        <div className="text-center mb-8">
          <div className="bg-[#556B2F]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-[#556B2F]" />
          </div>
          <p className="eyebrow mb-2">주문 접수 완료</p>
          <h2 className="h-display text-3xl mb-2">주문이 접수되었습니다</h2>
          <p className="text-[#1A1A1A]/55 text-sm">Заказ принят. Ожидает оплаты.</p>
        </div>

        <div className="space-y-3 mb-8 bg-[#F2EFE6]/50 rounded-lg p-5">
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#1A1A1A]/60">주문번호 · {t('orderNumber')}</span>
            <button onClick={() => copy(success.order_number)} className="font-mono font-bold text-base flex items-center gap-1.5 hover:text-[#556B2F]" data-testid="success-order-number">
              {success.order_number} <Copy className="h-3 w-3" />
            </button>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#1A1A1A]/60">결제금액 · {t('totalAmount')}</span>
            <span className="font-bold cff-text-numeric">{formatCurrency(success.total_amount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#1A1A1A]/60">발송 예정일</span>
            <span className="cff-text-numeric">{new Date(success.planned_shipment_date).toLocaleDateString('ko-KR')}</span>
          </div>
        </div>

        {/* BANK TRANSFER INFO */}
        <div className="border-2 border-[#C9A352]/40 rounded-lg p-6 mb-6 bg-[#F6F4EE]" data-testid="bank-info">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-[#C9A352]" />
            <p className="eyebrow text-[#1A1A1A]">아래 계좌로 입금 부탁드립니다</p>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-[#1A1A1A]/60">은행</span>
              <span className="font-bold" data-testid="bank-name">{bank?.bank_name || '농협'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#1A1A1A]/60">예금주</span>
              <span className="font-bold" data-testid="bank-holder">{bank?.bank_account_holder || 'Tyan Dmitriy'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#1A1A1A]/60">계좌번호</span>
              <button
                onClick={() => bank?.bank_account && copy(bank.bank_account, '계좌번호 복사됨')}
                className="font-mono font-bold flex items-center gap-1.5 hover:text-[#556B2F]"
                data-testid="bank-account"
              >
                {bank?.bank_account || '—'} <Copy className="h-3 w-3" />
              </button>
            </div>
          </div>
          <p className="text-xs text-[#8E2A4E] mt-4 pt-4 border-t border-[#C9A352]/20">
            ⚠️ 입금자명에는 <strong>{success.order_number}</strong> 또는 매장명을 입력해 주세요.
          </p>
        </div>

        <p className="text-xs text-[#1A1A1A]/45 text-center mb-6">
          📱 입금 안내 SMS가 등록된 휴대폰 번호로 전송되었습니다.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={() => navigate('/client/orders')} className="flex-1 cff-button-outline" data-testid="view-orders-btn">
            {t('orderHistory')}
          </Button>
          <Button onClick={() => setSuccess(null)} className="flex-1 cff-button-primary" data-testid="new-order-again-btn">
            {t('newOrder')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
